import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import {
  SessionModel,
  SubmissionModel,
  VisitLiteModel,
} from 'src/formio-custom-components/src/lib/shared/model';
import { FormioAppConfig } from 'dp-formio';
import { CommonQuestionModel, QuestionsPayload, ResourceQuestion } from '../models';

@Injectable()
export class CommonQuestionsService {
  private baseUrl: string;
  private getVisitInfoPath: string;
  private questionsPath: string;
  private masarVisitPath: string;
  private postPath: string;
  private getSessionsPath: string;
  private deletePath: string;

  constructor(protected http: HttpClient, private formioAppConfig: FormioAppConfig) {
    this._prepareUrls();
  }

  private _prepareUrls() {
    this.baseUrl = this.formioAppConfig.gateway;

    const endpoints = this.formioAppConfig.endpoints;
    this.getVisitInfoPath = endpoints?.visits + 'visit-key/';
    this.questionsPath = endpoints?.commonQuestions?.get;
    this.postPath = endpoints?.commonQuestions?.post;
    this.deletePath = endpoints?.commonQuestions?.delete;
    this.masarVisitPath = endpoints?.masarVisit;
    this.getSessionsPath = endpoints?.visitSessions + 'visit/';
  }

  getVisitInfo(key: string): Observable<VisitLiteModel> {
    const url = this.baseUrl + this.getVisitInfoPath + key;
    const pahse = this.baseUrl + this.masarVisitPath + key + '/phase';
    return forkJoin([
      this.http.get<any>(url),
      this.http.get<any>(pahse, { responseType: 'text' as any }),
    ]).pipe(
      map(([data, phase]) => {
        return {
          visitKey: data.visitKey,
          id: data.id,
          requestId: data.requestId,
          visitNumber: data.visitNumber,
          phase: phase,
          visitType: data?.accreditationType,
        } as VisitLiteModel;
      })
    );
  }

  getResourceData(
    resourceName: string,
    payload: QuestionsPayload = {}
  ): Observable<ResourceQuestion[]> {
    const url = this.formioAppConfig.formio.appUrl + resourceName + '/submission';
    return this.http
      .get<SubmissionModel<ResourceQuestion>[]>(url, { params: payload })
      .pipe(
        map((submissions) =>
          submissions.map((sub) => {
            return {
              _id: sub._id,
              ...sub?.data,
            };
          })
        )
      );
  }

  getSessionsByVisitKey(visitKey: string): Observable<SessionModel[]> {
    const url = this.baseUrl + this.getSessionsPath + visitKey;
    return this.http.get<any>(url, {
      params: {
        page: '0',
        size: `${Number.MAX_SAFE_INTEGER}`,
      },
    });
  }

  getQuestions(payload: QuestionsPayload = {}): Observable<CommonQuestionModel[]> {
    const url = this.baseUrl + this.questionsPath;
    return this.http.get<any>(url, { params: payload });
  }

  createQuestion(payload: CommonQuestionModel): Observable<CommonQuestionModel> {
    const url = this.baseUrl + this.postPath;
    return this.http.post<any>(url, payload);
  }

  updateQuestion(
    payload: CommonQuestionModel
  ): Observable<CommonQuestionModel> {
    const { id, ...data } = payload;
    return this.deleteQuestion(id).pipe(switchMap((_) => this.createQuestion(data)));
  }

  deleteQuestion(id: number): Observable<any> {
    const url = this.baseUrl + this.deletePath + id;
    return this.http.delete(url);
  }
}
